// src/modules/app-views/dataSearch/pages/Search/helpers/index.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import translateX from 'utils/translateX';
import { toCapitalCase } from 'utils/helpers';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************

// Map entity profile to translation key
const PROFILE_TRANSLATION_MAP = {
  company: 'profile_company',
  customer: 'profile_customer',
  supplier: 'profile_supplier',
  collaborator: 'profile_collaborator',
  candidate: 'profile_candidate',
  carrier: 'profile_carrier',
  owner: 'profile_owner',
  system_user: 'profile_system_user',
  operator: 'profile_operator',
  contact: 'profile_contact',
  user: 'profile_user',
  traveller: 'profile_traveller',
  subscription: 'profile_subscription',
  associate: 'profile_associate',
  partner: 'profile_partner',
  opportunity: 'profile_opportunity',
  organization: 'profile_organization',
};

// *******************************************************************************
// EXPORTS
// *******************************************************************************

/**
 * Build user message content from search values
 */
export function buildUserMessageContent(values, engine) {
  const { searchValue } = values;
  let content = `• ${translateX('search')}: ${searchValue}`;
  if (engine) content += `\n• ${translateX('engine')}: ${engine.toUpperCase()}`;
  return content;
}

/**
 * Format CNPJ to display format
 */
export function formatCnpj(value) {
  if (!value) return '';
  // Ensure value is string before calling replace
  const strValue = typeof value === 'string' ? value : String(value);
  const cnpj = strValue.replace(/\D/g, '');
  if (cnpj.length !== 14) return strValue;
  return cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
}

/**
 * Format CPF to display format
 */
export function formatCpf(value) {
  if (!value) return '';
  // Ensure value is string before calling replace
  const strValue = typeof value === 'string' ? value : String(value);
  const cpf = strValue.replace(/\D/g, '');
  if (cpf.length !== 11) return strValue;
  return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
}

/**
 * Format phone number
 */
export function formatPhone(value) {
  if (!value) return '';
  // Ensure value is string before calling replace
  const phone = (typeof value === 'string' ? value : String(value)).replace(/\D/g, '');
  if (phone.length === 11) {
    return phone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  }
  if (phone.length === 10) {
    return phone.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }
  return value;
}

/**
 * Format CEP to display format (XXXXX-XXX)
 */
export function formatCep(value) {
  if (!value) return '';
  // Ensure value is string before calling replace
  const strValue = typeof value === 'string' ? value : String(value);
  const cep = strValue.replace(/\D/g, '');
  if (cep.length !== 8) return strValue;
  return cep.replace(/(\d{5})(\d{3})/, '$1-$2');
}

/**
 * Format price to BRL currency
 */
export function formatPrice(value, showZeroIfEmpty = false) {
  if (value === null || value === undefined || isNaN(value)) {
    if (showZeroIfEmpty) {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(0);
    }
    return '';
  }
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

/**
 * Build natural language intro for searched entity
 */
export function buildNaturalIntro(data, alreadyExists, existingEntityProfile) {
  const isCompany = data?.entityType === 'company';
  const name = data?.name || '';
  const fullName = data?.fullName || '';
  const status = data?.status;

  let intro = '';

  if (alreadyExists) {
    // Filter out 'search' to check if there are displayable profiles
    const displayableProfiles = (existingEntityProfile || []).filter(p => p !== 'search');

    if (displayableProfiles.length > 0) {
      const profilesText = formatProfileList(existingEntityProfile);
      intro = translateX('search_intro_already_exists_with_profiles', {
        name: toCapitalCase(name || fullName),
        profiles: profilesText,
      });
    } else {
      intro = translateX('search_intro_already_exists', {
        name: toCapitalCase(name || fullName),
      });
    }
  } else if (isCompany) {
    intro = translateX('search_intro_found_company', {
      name: toCapitalCase(name || fullName),
    });
  } else {
    intro = translateX('search_intro_found_person', {
      name: toCapitalCase(name || fullName),
    });
  }

  // Add status if available
  if (status) {
    const statusText = status === 'ATIVA' ? translateX('active_status') : translateX('inactive_status');
    intro += ' ' + translateX('search_intro_status', { status: statusText });
  }

  // Add main activity if company
  if (isCompany && data?.mainActivity?.description) {
    intro += ' ' + translateX('search_intro_activity', {
      activity: toCapitalCase(data.mainActivity.description),
    });
  }

  return intro;
}

/**
 * Build error details from API response
 */
export function buildErrorDetails({ message, code, details, statusCode, isNetworkError }) {
  const result = {
    explanationKey: '',
    explanationFallback: '',
    technicalMessage: message || '',
    suggestionKeys: [],
  };

  // Network errors
  if (isNetworkError) {
    result.explanationKey = 'error_network_connection';
    result.suggestionKeys = ['suggestion_check_internet', 'suggestion_try_again_later'];
    return result;
  }

  // Handle specific error codes
  if (code) {
    switch (code) {
      case 'CNPJ_NOT_FOUND':
        result.explanationKey = 'error_cnpj_not_found';
        result.suggestionKeys = ['suggestion_check_cnpj', 'suggestion_try_different_cnpj'];
        break;
      case 'CPF_NOT_FOUND':
        result.explanationKey = 'error_cpf_not_found';
        result.suggestionKeys = ['suggestion_check_cpf', 'suggestion_try_different_cpf'];
        break;
      case 'INVALID_CNPJ':
        result.explanationKey = 'error_invalid_cnpj';
        result.suggestionKeys = ['suggestion_check_cnpj_format'];
        break;
      case 'INVALID_CPF':
        result.explanationKey = 'error_invalid_cpf';
        result.suggestionKeys = ['suggestion_check_cpf_format'];
        break;
      case 'SERVICE_UNAVAILABLE':
        result.explanationKey = 'error_service_unavailable';
        result.suggestionKeys = ['suggestion_try_again_later'];
        break;
      case 'RATE_LIMIT':
        result.explanationKey = 'error_rate_limit';
        result.suggestionKeys = ['suggestion_wait_few_minutes'];
        break;
      case 'INSUFFICIENT_CREDITS':
        result.explanationKey = 'error_insufficient_credits';
        result.suggestionKeys = ['suggestion_add_credits', 'suggestion_contact_support'];
        break;
      case 'ENGINE_DISABLED':
        result.explanationKey = 'error_engine_disabled';
        result.suggestionKeys = ['suggestion_try_different_engine'];
        break;
      default:
        result.explanationKey = 'error_processing_request';
        result.technicalMessage = `[${code}] ${message || ''}`;
        result.suggestionKeys = ['suggestion_try_again', 'suggestion_contact_support_if_persists'];
    }
    return result;
  }

  // Handle HTTP status codes
  if (statusCode) {
    switch (statusCode) {
      case 400:
        result.explanationKey = 'error_invalid_request';
        result.suggestionKeys = ['suggestion_check_search_data'];
        break;
      case 401:
      case 403:
        result.explanationKey = 'error_authentication';
        result.suggestionKeys = ['suggestion_refresh_page', 'suggestion_login_again'];
        break;
      case 404:
        result.explanationKey = 'error_not_found';
        result.suggestionKeys = ['suggestion_check_search_data'];
        break;
      case 429:
        result.explanationKey = 'error_too_many_requests';
        result.suggestionKeys = ['suggestion_wait_few_minutes'];
        break;
      case 500:
      case 502:
      case 503:
        result.explanationKey = 'error_server_error';
        result.suggestionKeys = ['suggestion_try_again_later', 'suggestion_contact_support_if_persists'];
        break;
      default:
        result.explanationKey = 'error_unexpected';
        result.suggestionKeys = ['suggestion_try_again', 'suggestion_contact_support_if_persists'];
    }
    return result;
  }

  // Fallback: try to interpret the message
  if (message) {
    const lowerMessage = message.toLowerCase();

    if (lowerMessage.includes('timeout') || lowerMessage.includes('tempo')) {
      result.explanationKey = 'error_timeout';
      result.suggestionKeys = ['suggestion_try_again'];
    } else if (lowerMessage.includes('cnpj')) {
      result.explanationKey = 'error_cnpj_issue';
      result.suggestionKeys = ['suggestion_check_cnpj'];
    } else if (lowerMessage.includes('cpf')) {
      result.explanationKey = 'error_cpf_issue';
      result.suggestionKeys = ['suggestion_check_cpf'];
    } else {
      result.explanationFallback = message;
      result.suggestionKeys = ['suggestion_try_again', 'suggestion_contact_support_if_persists'];
    }
    return result;
  }

  // Ultimate fallback
  result.explanationKey = 'error_unexpected';
  result.suggestionKeys = ['suggestion_try_again', 'suggestion_contact_support'];

  return result;
}

// *******************************************************************************
// HELPERS (internal)
// *******************************************************************************

/**
 * Format profile list for display (e.g., "'Empresa', 'Fornecedor' e 'Cliente'")
 */
function formatProfileList(profiles) {
  if (!profiles || profiles.length === 0) return '';

  // Filter out 'search' profile and translate
  const translatedProfiles = profiles
    .filter(p => p !== 'search')
    .map(p => `'${translateX(PROFILE_TRANSLATION_MAP[p] || p)}'`);

  if (translatedProfiles.length === 0) return '';
  if (translatedProfiles.length === 1) return translatedProfiles[0];

  // Join with comma and "e" before last item (Portuguese format)
  const lastProfile = translatedProfiles.pop();
  return `${translatedProfiles.join(', ')} ${translateX('and')} ${lastProfile}`;
}
